import * as React from 'react'

import {
  action,
  IReactionDisposer,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from 'mobx'
import { inject, observer } from 'mobx-react'

import ExportMapButton from '~/client/src/desktop/components/MapExport/ExportMapButton'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import LogisticsMapView from '~/client/src/shared/components/LogisticsMapView/LogisticsMapView'
import LogisticsMapViewSetUpStore from '~/client/src/shared/components/LogisticsMapView/LogisticsMapViewSetUp.store'
import { runWithMarkupSessionGuard } from '~/client/src/shared/components/LogisticsMapView/MapMarkupSession.store'
import { createMapExportEntry } from '~/client/src/shared/components/MapExport/createMapExportEntry'
import type { IExportWorkflowSource } from '~/client/src/shared/components/MapExport/ExportPreview.store'
import MapExportStore from '~/client/src/shared/components/MapExport/MapExport.store'
import { isMapExportEnvironment } from '~/client/src/shared/components/MapExport/mapExportEnvironment'
import MapViewsGallery from '~/client/src/shared/components/SitemapsGallery/MapViewsGallery'
import GlobeView from '~/client/src/shared/models/GlobeView'
import Sitemap from '~/client/src/shared/models/Sitemap'
import BasemapsStore from '~/client/src/shared/stores/domain/Basemaps.store'
import GlobeViewsStore from '~/client/src/shared/stores/domain/GlobeViews.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import MarkupsStore from '~/client/src/shared/stores/domain/Markups.store'
import SitemapItemsStore from '~/client/src/shared/stores/domain/SitemapItems.store'
import SitemapsStore from '~/client/src/shared/stores/domain/Sitemaps.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import TilesetsStore from '~/client/src/shared/stores/domain/Tilesets.store'
import { SAVE_WORKFLOWS_CONFIGURATIONS } from '~/client/src/shared/stores/EventStore/eventConstants'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'
import WorkflowDrawingGuardStore from '~/client/src/shared/stores/ui/WorkflowDrawingGuard.store'
import { createMarkupsVisibilityKeyHandler } from '~/client/src/shared/utils/createMarkupsVisibilityKeyHandler'
import { isBaseSurfaceActive } from '~/client/src/shared/utils/keyboardClaim'

import LogisticsStore from '../Logistics/Logistics.store'
import LogisticsListStore from '../LogisticsList/LogisticsList.store'
import LogisticsSideBar from '../LogisticsSideBar'
import LogisticsMapStore from './LogisticsMap.store'

// Loaded on first open: the export preview and its label engine stay out of the Workflows map.
const ExportPreview = React.lazy(
  () => import('~/client/src/shared/components/MapExport/ExportPreview'),
)

interface IProps {
  store: LogisticsStore
  logisticsMapStore: LogisticsMapStore
  mapExportStore: MapExportStore

  state?: DesktopInitialState
  sitemapsStore?: SitemapsStore
  basemapsStore?: BasemapsStore
  sitemapItemsStore?: SitemapItemsStore
  locationAttributesStore?: LocationAttributesStore
  tagsStore?: TagsStore

  globeViewsStore?: GlobeViewsStore
  eventsStore?: EventsStore
  tilesetsStore?: TilesetsStore
  logisticsListStore?: LogisticsListStore
  markupsStore?: MarkupsStore
  workflowDrawingGuardStore?: WorkflowDrawingGuardStore
}

/* eslint-disable */
@inject(
  'state',
  'sitemapsStore',
  'basemapsStore',
  'sitemapItemsStore',
  'locationAttributesStore',
  'tagsStore',
  'globeViewsStore',
  'eventsStore',
  'tilesetsStore',
  'markupsStore',
  'workflowDrawingGuardStore',
)
@observer
export default class LogisticsMap extends React.Component<IProps> {
  public readonly store: LogisticsMapViewSetUpStore = null
  private readonly mapActions: React.ReactNode = null
  // One object for the map's life, so the open export's observers don't re-render on every host render.
  private readonly workflowSource: IExportWorkflowSource
  private disposeDisplayedPermitIdReaction: IReactionDisposer
  @observable private localProps: IProps = null

  public constructor(props: IProps) {
    super(props)

    this.store = new LogisticsMapViewSetUpStore(
      props.sitemapsStore,
      props.tilesetsStore,
      props.globeViewsStore,
      props.basemapsStore,
      props.sitemapItemsStore,
      props.locationAttributesStore,
      props.tagsStore,
      props.state,
      props.store,
      props.eventsStore,
      () => props.store.displayedWorkflow?.id,
      props.markupsStore,
    )

    const config = props.state.workflowsMapIdsList

    const isGlobeMode = !!config?.[0]?.globeViewId
    if (isGlobeMode) {
      const globe = props.globeViewsStore.byId.get(config?.[0]?.globeViewId)
      this.store.globeViewControlStore.selectGlobe(globe)
    } else {
      const whiteboard = props.sitemapsStore.byId.get(config?.[0]?.sitemapId)
      this.store.selectSitemap(whiteboard)
      this.store.mapBoxViewerStore.setViewportFromAddress()
    }

    this.workflowSource = {
      logisticsStore: props.store,
      logisticsMapStore: props.logisticsMapStore,
    }
    if (isMapExportEnvironment()) {
      this.mapActions = (
        <ExportMapButton mapExportStore={props.mapExportStore} size="lg" />
      )
    }

    this.localProps = props
    makeObservable(this)
  }

  public componentDidMount() {
    this.localProps.store.setInitialDateMode()
    this.store.mapBoxViewerStore.markupPrefs.setWorkflowMarkupsSupported(
      true,
      !!this.props.state.userActiveProjectSettings?.isAdmin,
    )
    window.addEventListener('keydown', this.handleMarkupsShortcut)
    this.props.mapExportStore.setEntry(
      createMapExportEntry(this.store, this.props.state, this.runGuarded),
    )

    this.disposeDisplayedPermitIdReaction = reaction(
      () => this.localProps.store.displayedWorkflow?.id,
      displayedPermitId => {
        if (!displayedPermitId) {
          this.store.deselectWorkflows()
        }
      },
      { fireImmediately: true },
    )
  }

  public componentWillUnmount() {
    this.props.mapExportStore.setEntry(null)
    this.props.mapExportStore.close()
    this.disposeDisplayedPermitIdReaction?.()
    this.store.dispose()
    this.store.mapBoxViewerStore.markupPrefs.setWorkflowMarkupsSupported(false)
    window.removeEventListener('keydown', this.handleMarkupsShortcut)
    runInAction(() => (this.props.state.preSelectedSiteLocation = null))
  }

  // Press "A" to toggle MY workflow-markups visibility. A plain letter (no
  // modifier) avoids browser-reserved combos.
  private readonly handleMarkupsShortcut = createMarkupsVisibilityKeyHandler({
    // Mid-interaction the context layers must stay put; the editor owns the keys.
    isActive: () =>
      isBaseSurfaceActive() && !this.store.markupSession?.isInteracting,
    onToggle: () =>
      this.store.mapBoxViewerStore.markupPrefs.toggleWorkflowMarkups(),
  })

  public render() {
    return <div className="row full-height relative">{this.content}</div>
  }

  private get content() {
    const {
      store,
      store: { state },
      logisticsMapStore,
      logisticsListStore,
      mapExportStore,
    } = this.localProps
    const { openFormTypeSelectionForm } = store

    return (
      <>
        <LogisticsSideBar
          store={store}
          logisticsMapViewSetUpStore={this.store}
          mapStore={logisticsMapStore}
          logisticsListStore={logisticsListStore}
        />
        <div className="col full-height">
          <div className="full-height relative">
            <MapViewsGallery
              mapIds={state.workflowsMapIdsList}
              selectedGlobeViewId={
                this.store.globeViewControlStore.selectedGlobeView?.id
              }
              selectedWhiteboardId={this.store.sitemap?.id}
              renderMapViewComponent={this.renderMapViewComponent}
              eventName={SAVE_WORKFLOWS_CONFIGURATIONS}
              selectGlobe={this.selectGlobeAndCloseModals}
              systemMapSwitchNonce={this.store.systemMapSwitchNonce}
              selectSitemap={this.selectSitemapAndCloseModals}
              mapReturn={this.mapReturn}
              shouldUseFullHeight={true}
              shouldShowCreateNewButton={
                !this.store.markupSession?.isInteracting
              }
              openFormTypeSelectionForm={openFormTypeSelectionForm}
              mapActions={this.mapActions}
              mapBoxViewerStore={this.store.mapBoxViewerStore}
              shouldDisableZoom={true}
            />
            {mapExportStore.isOpen && (
              <React.Suspense fallback={null}>
                <ExportPreview
                  exportStore={mapExportStore}
                  liveSetUpStore={this.store}
                  workflowSource={this.workflowSource}
                />
              </React.Suspense>
            )}
          </div>
        </div>
      </>
    )
  }

  private renderMapViewComponent = (
    fullscreenTargetRef?: React.RefObject<HTMLElement>,
  ): JSX.Element => {
    const {
      logisticsMapStore,
      store: {
        showWorkflowViewDialog,
        openFormTypeSelectionForm,
        openWorkflowCreationForm,
        isFormTypeSelectionActive,
        isPermitCreationActive,
      },
      store,
    } = this.localProps

    return (
      <div className="sitemap-container full-width full-height">
        <LogisticsMapView
          globe={this.store.globeViewControlStore.selectedGlobeView}
          sitemap={this.store.sitemap}
          store={this.store}
          mapBoxViewerStore={this.store.mapBoxViewerStore}
          logisticsStore={store}
          openPermit={showWorkflowViewDialog}
          selectGlobe={this.selectGlobeAndCloseModals}
          selectSitemap={this.selectSitemapAndCloseModals}
          logisticsMapStore={logisticsMapStore}
          openFormTypeSelectionForm={openFormTypeSelectionForm}
          openWorkflowCreationForm={openWorkflowCreationForm}
          isFormTypeSelectionActive={isFormTypeSelectionActive}
          isPermitCreationActive={isPermitCreationActive}
          fullscreenTargetRef={fullscreenTargetRef}
        />
      </div>
    )
  }

  @action.bound
  private selectGlobeAndCloseModals(globe: GlobeView, onSwitched?: () => void) {
    this.runGuarded(() => this.store.switchToGlobe(globe, onSwitched))
  }

  @action.bound
  private selectSitemapAndCloseModals(
    sitemap: Sitemap,
    onSwitched?: () => void,
  ) {
    this.runGuarded(() => this.store.switchToSitemap(sitemap, onSwitched))
  }

  private get mapReturn(): { name: string; back: () => void } | null {
    return this.store.mapReturnLink({
      selectGlobe: this.selectGlobeAndCloseModals,
      selectSitemap: this.selectSitemapAndCloseModals,
    })
  }

  // Any map-view pill switch swaps the mapbox stage out from under terra-draw.
  private runGuarded = (proceed: () => void): void => {
    runWithMarkupSessionGuard(
      this.store.markupSession,
      this.props.workflowDrawingGuardStore,
      proceed,
    )
  }
}
